import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import {
  CharacterCount,
  ColumnLayoutRow,
  ColumnLayoutRows,
  FormControl,
  FormDialog,
  InputLabel,
  MenuItem as SelectMenuItem,
  Select,
  TextField,
  Typography,
} from '@/shared/ui';
import { typography } from '@/shared/theme/tokens';
import { toast } from '@/shared/feedback/toast';
import type { NavMenuItem, NavRailDef, RailId } from '@/shared/layout/nav';
import type { NavWorkspaceEntry, NavWorkspacePreference, NavWorkspaceRail } from '@/shared/layout/nav/workspace';
import type { NavWorkspaceWrite } from './navWorkspace.api';

const NAME_MAX = 40;

const newWorkspaceId = (): string => {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return `ws${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
};

interface DraftItem {
  path: string;
  label: string;
  shown: boolean;
}

interface DraftRail {
  id: RailId;
  label: string;
  shown: boolean;
  items: DraftItem[];
}

const blankWorkspace = (id: string, name: string): NavWorkspaceEntry => ({
  id,
  name,
  hidden: [],
  order: [],
  rails: [],
  startPath: '',
});

const selectIconSx = {
  '&:not(:hover):not(:focus-within) .MuiSelect-icon': { display: 'none' },
  '&:not(:hover):not(:focus-within) .MuiSelect-select': { pr: '8px !important' },
} as const;

interface WorkspaceSwitcherProps {
  collapsed: boolean;
  preference: NavWorkspacePreference | undefined;
  catalogRails: NavRailDef[];
  catalogItems: Partial<Record<RailId, NavMenuItem[]>>;
  busy: boolean;
  onSave: (payload: NavWorkspaceWrite) => Promise<unknown>;
}

/**
 * Workspace picker under the brand mark. A workspace reorders and hides rails
 * the user can already open, and chooses the page opened after login.
 * The built-in default workspace only stores that start page.
 * Hidden while the rail is collapsed.
 */
const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({
  collapsed,
  preference,
  catalogRails,
  catalogItems,
  busy,
  onSave,
}) => {
  const { t } = useTranslation('navigation');
  const { t: tCommon } = useTranslation('common');
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [startPath, setStartPath] = useState('');
  const [draft, setDraft] = useState<DraftRail[]>([]);

  const workspaces = preference?.workspaces ?? [];
  const active = workspaces.find((entry) => entry.id === preference?.activeId) ?? workspaces[0];
  const label = active?.name?.trim() || t('workspaceDefault');

  const persist = async (payload: NavWorkspaceWrite): Promise<boolean> => {
    try {
      await onSave(payload);
      return true;
    } catch {
      toast.error(t('workspaceSaveFailed'));
      return false;
    }
  };

  const named = (entries: NavWorkspaceEntry[]): NavWorkspaceEntry[] =>
    entries.map((entry) => ({
      ...entry,
      name: entry.name.trim() || t('workspaceDefault'),
      startPath: entry.startPath ?? '',
    }));

  const openEditor = (current: NavWorkspaceEntry, isNew: boolean) => {
    setAnchor(null);
    setEditingId(current.id);
    setCreating(isNew);
    const hiddenRails = new Set(current.hidden);
    const byId = new Map(catalogRails.map((rail) => [rail.id, rail]));
    const rows: DraftRail[] = [];
    const seen = new Set<string>();
    const push = (id: string) => {
      const rail = byId.get(id as RailId);
      if (!rail || seen.has(rail.id)) return;
      seen.add(rail.id);
      const pref = current.rails?.find((entry) => entry.id === rail.id);
      const hiddenItems = new Set(pref?.hiddenItems ?? []);
      const source = catalogItems[rail.id] ?? [];
      const itemByPath = new Map(source.map((item) => [item.path, item]));
      const items: DraftItem[] = [];
      const seenPaths = new Set<string>();
      const pushItem = (path: string) => {
        const item = itemByPath.get(path);
        if (!item || seenPaths.has(path)) return;
        seenPaths.add(path);
        items.push({
          path,
          label: t(item.labelKey),
          shown: !hiddenItems.has(path),
        });
      };
      for (const path of pref?.itemOrder ?? []) pushItem(path);
      for (const item of source) pushItem(item.path);
      rows.push({
        id: rail.id,
        label: t(rail.labelKey),
        shown: !hiddenRails.has(rail.id),
        items,
      });
    };
    for (const id of current.order) push(id);
    for (const rail of catalogRails) push(rail.id);
    setName(isNew ? '' : current.name.trim() || t('workspaceDefault'));
    setStartPath(isNew ? '' : current.startPath ?? '');
    setDraft(rows);
    setEditorOpen(true);
  };

  const editingDefault = !creating && editingId === 'default';
  const startOptions = useMemo(() => {
    const options: { path: string; label: string }[] = [];
    const seen = new Set<string>();
    for (const row of draft) {
      if (!row.shown) continue;
      const pages = row.items.length > 1 ? row.items.filter((item) => item.shown) : row.items;
      for (const item of pages) {
        if (seen.has(item.path) || item.path === '/dashboard') continue;
        seen.add(item.path);
        options.push({ path: item.path, label: item.label });
      }
    }
    return options;
  }, [draft]);

  useEffect(() => {
    if (!editorOpen || !startPath) return;
    if (startOptions.some((option) => option.path === startPath)) return;
    setStartPath('');
  }, [editorOpen, startOptions, startPath]);

  const moveRail = (activeId: string, overId: string) => {
    setDraft((prev) => {
      const from = prev.findIndex((row) => row.id === activeId);
      const to = prev.findIndex((row) => row.id === overId);
      if (from < 0 || to < 0 || from === to) return prev;
      const next = prev.slice();
      const [row] = next.splice(from, 1);
      next.splice(to, 0, row);
      return next;
    });
  };

  const moveItem = (railId: RailId, activeId: string, overId: string) => {
    setDraft((prev) =>
      prev.map((row) => {
        if (row.id !== railId) return row;
        const from = row.items.findIndex((item) => item.path === activeId);
        const to = row.items.findIndex((item) => item.path === overId);
        if (from < 0 || to < 0 || from === to) return row;
        const items = row.items.slice();
        const [item] = items.splice(from, 1);
        items.splice(to, 0, item);
        return { ...row, items };
      }),
    );
  };

  const toggleRail = (railId: RailId) => {
    setDraft((prev) =>
      prev.map((row) => {
        if (row.id !== railId) return row;
        const shown = !row.shown;
        const items = shown && row.items.every((item) => !item.shown)
          ? row.items.map((item) => ({ ...item, shown: true }))
          : row.items;
        return { ...row, shown, items };
      }),
    );
  };

  const toggleItem = (railId: RailId, path: string) => {
    setDraft((prev) =>
      prev.map((row) => {
        if (row.id !== railId) return row;
        const items = row.items.map((item) =>
          item.path === path ? { ...item, shown: !item.shown } : item,
        );
        const shownItems = items.filter((item) => item.shown);
        return { ...row, shown: shownItems.length > 0, items };
      }),
    );
  };

  const saveEditor = () => {
    const base = workspaces.length
      ? workspaces
      : [blankWorkspace('default', t('workspaceDefault'))];
    const id = editingId ?? 'default';
    const rails: NavWorkspaceRail[] = draft
      .filter((row) => row.items.length > 1)
      .map((row) => ({
        id: row.id,
        hiddenItems: row.items.filter((item) => !item.shown).map((item) => item.path),
        itemOrder: row.items.map((item) => item.path),
        homePath: '',
      }));
    const existing = base.find((entry) => entry.id === id);
    const nextEntry: NavWorkspaceEntry = id === 'default' && !creating
      ? {
          id,
          name: name.trim() || t('workspaceDefault'),
          hidden: existing?.hidden ?? [],
          order: existing?.order ?? [],
          rails: existing?.rails ?? [],
          startPath,
        }
      : {
          id,
          name: name.trim() || t('workspaceDefault'),
          hidden: draft.filter((row) => !row.shown).map((row) => row.id),
          order: draft.map((row) => row.id),
          rails,
          startPath,
        };
    const next = creating
      ? [...named(base), nextEntry]
      : named(base.map((entry) => (entry.id === id ? nextEntry : entry)));
    void persist({ activeId: id, workspaces: next }).then((ok) => {
      if (!ok) return;
      setEditorOpen(false);
      toast.success(t('workspaceSaved'));
    });
  };

  const selectWorkspace = (id: string) => {
    setAnchor(null);
    if (!preference || id === preference.activeId) return;
    void persist({ activeId: id, workspaces: named(preference.workspaces) });
  };

  const openCreate = () => {
    if (workspaces.length >= 12) return;
    openEditor(blankWorkspace(newWorkspaceId(), ''), true);
  };

  const removeWorkspace = () => {
    if (!preference || preference.workspaces.length < 2 || !editingId || creating) return;
    const remaining = named(preference.workspaces.filter((entry) => entry.id !== editingId));
    void persist({
      activeId: remaining[0].id,
      workspaces: remaining,
    }).then(() => setEditorOpen(false));
  };

  if (collapsed) return null;

  const button = (
    <Box
      component="button"
      type="button"
      aria-label={t('workspaceSwitch')}
      aria-haspopup="menu"
      onClick={(event) => setAnchor(event.currentTarget)}
      sx={{
        width: 'calc(100% - 16px)',
        mx: 1,
        mb: 0.5,
        mt: 0.25,
        minHeight: 36,
        px: 1.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 1,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        color: 'text.primary',
        cursor: 'pointer',
        font: 'inherit',
        textAlign: 'left',
      }}
    >
      <SpaceDashboardOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: typography.size.sm,
          fontWeight: 600,
        }}
      >
        {label}
      </Box>
      <KeyboardArrowDownIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
    </Box>
  );

  return (
    <>
      {button}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        marginThreshold={0}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: anchor ? anchor.offsetWidth : undefined,
              minWidth: anchor ? anchor.offsetWidth : undefined,
              maxWidth: anchor ? anchor.offsetWidth : undefined,
              boxSizing: 'border-box',
            },
          },
          list: { sx: { py: 0.5, width: '100%' } },
        }}
      >
        {(workspaces.length ? workspaces : [blankWorkspace('default', '')]).map((entry) => (
          <MenuItem
            key={entry.id}
            selected={entry.id === (active?.id ?? 'default')}
            onClick={() => selectWorkspace(entry.id)}
            sx={{ px: 1.25, minHeight: 36 }}
          >
            <ListItemText
              primary={entry.name.trim() || t('workspaceDefault')}
              slotProps={{ primary: { noWrap: true, variant: 'body2' } }}
            />
            <IconButton
              size="small"
              aria-label={entry.id === 'default' ? t('workspaceHomeTitle') : tCommon('settings')}
              onClick={(event) => {
                event.stopPropagation();
                openEditor(entry, false);
              }}
            >
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </MenuItem>
        ))}
        <MenuItem onClick={openCreate} disabled={busy || workspaces.length >= 12} sx={{ px: 1.25, minHeight: 36 }}>
          {t('workspaceAdd')}
        </MenuItem>
      </Menu>
      <FormDialog
        open={editorOpen}
        title={
          creating
            ? t('workspaceAdd')
            : editingDefault
              ? t('workspaceHomeTitle')
              : t('workspaceCustomize')
        }
        busy={busy}
        onClose={() => setEditorOpen(false)}
        onSubmit={saveEditor}
        submitLabel={tCommon('save')}
        busySubmitLabel={tCommon('saving')}
        minHeight={editingDefault ? 280 : 420}
        startSlot={
          !creating && editingId !== 'default' && workspaces.length > 1 ? (
            <Box
              component="button"
              type="button"
              disabled={busy}
              onClick={removeWorkspace}
              sx={{
                border: 0,
                bgcolor: 'transparent',
                color: 'error.main',
                cursor: 'pointer',
                font: 'inherit',
                fontSize: typography.size.sm,
              }}
            >
              {t('workspaceDelete')}
            </Box>
          ) : undefined
        }
      >
        <TextField
          label={t('workspaceName')}
          placeholder={t('workspaceNamePlaceholder')}
          value={name}
          onChange={(event) => setName(event.target.value.slice(0, NAME_MAX))}
          labelEnd={<CharacterCount current={name.length} max={NAME_MAX} />}
          slotProps={{ htmlInput: { maxLength: NAME_MAX } }}
          fullWidth
          sx={{ mb: 2 }}
        />
        <FormControl fullWidth sx={{ mb: 2, ...selectIconSx }}>
          <InputLabel shrink>{t('workspaceHome')}</InputLabel>
          <Select
            value={startOptions.some((option) => option.path === startPath) ? startPath : ''}
            label={t('workspaceHome')}
            displayEmpty
            notched
            onChange={(event) => setStartPath(String(event.target.value))}
            renderValue={(selected) => {
              const path = String(selected);
              if (!path) return t('dashboard');
              return startOptions.find((option) => option.path === path)?.label ?? t('dashboard');
            }}
          >
            <SelectMenuItem value="">{t('dashboard')}</SelectMenuItem>
            {startOptions.map((option) => (
              <SelectMenuItem key={option.path} value={option.path}>
                {option.label}
              </SelectMenuItem>
            ))}
          </Select>
        </FormControl>
        {editingDefault ? null : (
          <>
            <Typography variant="h6" sx={{ mb: 1 }}>{t('workspaceRails')}</Typography>
            <ColumnLayoutRows onReorder={moveRail}>
              {draft.map((row) => (
                <Box key={row.id}>
                  <ColumnLayoutRow
                    id={row.id}
                    label={row.label}
                    visible={row.shown}
                    onToggle={() => toggleRail(row.id)}
                  />
                  {row.items.length > 1 ? (
                    <ColumnLayoutRows onReorder={(activeId, overId) => moveItem(row.id, activeId, overId)}>
                      {row.items.map((item) => (
                        <ColumnLayoutRow
                          key={item.path}
                          id={item.path}
                          label={item.label}
                          visible={item.shown}
                          inset
                          onToggle={() => toggleItem(row.id, item.path)}
                        />
                      ))}
                    </ColumnLayoutRows>
                  ) : null}
                </Box>
              ))}
            </ColumnLayoutRows>
          </>
        )}
      </FormDialog>
    </>
  );
};

export default WorkspaceSwitcher;
